import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WordleGrid from '../components/WordleGrid';
import Keyboard from '../components/Keyboard';
import WordNotFoundModal from '../components/WordNotFoundModal';
import { useSocketContext } from '../contexts/SocketContext';
import { useInvitationContext } from '../contexts/InvitationContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface GameState {
  gameId: number | null;
  gameType: string;
  wordLength: number;
  useUmlauts: boolean;
  attempts: Array<{
    attemptNumber: number;
    guess: string;
    feedback: string[];
    playerId?: number;
  }>;
  status: string;
  player1Id?: number;
  player2Id?: number;
  player1Username?: string;
  player2Username?: string;
  currentTurn?: number;
  currentUserId?: number;
  targetWord?: string;
  racePhase?: 'word_selection' | 'active';
  myWord?: string;
  opponentWord?: string;
  raceStartTime?: string;
  opponentProgress?: {
    attemptNumber: number;
    guessed: boolean;
  };
  winnerId?: number | null;
  nextResetTime?: string; // For daily wordle: time when next word will be available
}

const Game: React.FC = () => {
  const [searchParams] = useSearchParams();
  const gameType = searchParams.get('type');
  const gameMode = searchParams.get('mode');
  const gameIdParam = searchParams.get('gameId');
  const navigate = useNavigate();
  const { socket } = useSocketContext();
  const currentUserIdRef = useRef<number | null>(null);
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const gameLoadedRef = useRef<boolean>(false);

  const [gameState, setGameState] = useState<GameState | null>(null);
  const [currentGuess, setCurrentGuess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [keyboardFeedback, setKeyboardFeedback] = useState<{ [key: string]: 'green' | 'yellow' | 'gray' }>({});
  const [showWordNotFoundModal, setShowWordNotFoundModal] = useState(false);
  const [wordToAdd, setWordToAdd] = useState<{ word: string; wordLength: number; useUmlauts: boolean } | null>(null);
  const [showSolution, setShowSolution] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatingCellIndex, setAnimatingCellIndex] = useState(-1);
  const [animatingFeedback, setAnimatingFeedback] = useState<string[] | null>(null);
  const [selectedCellIndex, setSelectedCellIndex] = useState(0);
  const [opponentCurrentGuess, setOpponentCurrentGuess] = useState('');
  const [showOpponentProgress, setShowOpponentProgress] = useState(false);
  const [timeUntilNextDaily, setTimeUntilNextDaily] = useState<string>('');
  const [showRematchModal, setShowRematchModal] = useState(false);
  const [rematchStatus, setRematchStatus] = useState<'pending' | 'accepted' | 'declined' | null>(null);
  const [opponentRematchStatus, setOpponentRematchStatus] = useState<'pending' | 'accepted' | 'declined' | null>(null);
  const [showRaceWinnerModal, setShowRaceWinnerModal] = useState(false);
  const [raceLoserChoice, setRaceLoserChoice] = useState<'continue' | 'show-result' | null>(null);
  const [showRaceResult, setShowRaceResult] = useState(false);
  const [showNewGameModal, setShowNewGameModal] = useState(false);
  const [newGameStatus, setNewGameStatus] = useState<'pending' | 'accepted' | 'declined' | null>(null);
  const [opponentNewGameStatus, setOpponentNewGameStatus] = useState<'pending' | 'accepted' | 'declined' | null>(null);
  const [showWordNotInListModal, setShowWordNotInListModal] = useState(false);
  const [pendingRaceWord, setPendingRaceWord] = useState<string | null>(null);
  const previousLobbySettingsRef = useRef<{ wordLength: number; useUmlauts: boolean } | null>(null);
  const { pendingInvitation } = useInvitationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRaceLostModal, setShowRaceLostModal] = useState(false);

  // Helper to count logical letters (treat ß as a single character)
  // Normalize ß to ẞ before calculating length or uppercasing
  const normalizeSharpS = (str: string) => str.replace(/ß/g, 'ẞ');
  const getLogicalLength = (word: string) => normalizeSharpS(word).length;

  useEffect(() => {
    // Get current user ID
    fetch(`${API_URL}/auth/me`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.authenticated) {
          currentUserIdRef.current = data.user.id;
        }
      });

    if (gameType === 'solo') {
      loadSoloGame();
    } else if (gameType === 'multiplayer' && gameIdParam) {
      loadMultiplayerGame(parseInt(gameIdParam));
    } else if (gameType === 'multiplayer' && !gameIdParam) {
      // Wait for game-started event if no gameId in URL
      if (socket) {
        socket.once('game-started', (data: any) => {
          navigate(`/game?type=multiplayer&mode=${data.gameMode}&gameId=${data.gameId}`, { replace: true });
        });
      }
    } else {
      navigate('/menu');
    }
  }, [gameType, gameMode, gameIdParam]);

  useEffect(() => {
    if (!socket || gameType !== 'multiplayer') return;

    socket.on('game-started', (data: any) => {
      console.log('Received game-started:', data);
      // Update URL if needed
      if (!gameIdParam || gameIdParam !== data.gameId) {
        navigate(`/game?type=multiplayer&mode=${data.gameMode}&gameId=${data.gameId}`, { replace: true });
      }
      // Load game state
      loadMultiplayerGame(data.gameId);
    });

    socket.on('game-state', (data: any) => {
      console.log('Received game-state:', data);
      // Mark game as loaded
      gameLoadedRef.current = true;
      
      // Clear the timeout since game loaded successfully
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      
      const newGameState: GameState = {
        gameId: data.gameId,
        gameType: data.gameMode,
        wordLength: data.wordLength,
        useUmlauts: data.useUmlauts,
        attempts: data.attempts || [],
        status: data.status,
        player1Id: data.player1Id,
        player2Id: data.player2Id,
        player1Username: data.player1Username,
        player2Username: data.player2Username,
        currentTurn: data.currentTurn,
        currentUserId: currentUserIdRef.current || undefined,
        racePhase: (data.status === 'word_selection' ? 'word_selection' : 'active') as 'word_selection' | 'active',
        myWord: data.myWord,
        opponentWord: data.opponentWord,
        raceStartTime: data.raceStartTime,
        winnerId: data.winnerId || null
      };
      setGameState(newGameState);
      if (data.attempts && data.attempts.length > 0) {
        updateKeyboardFeedback(data.attempts);
      }
      setLoading(false);
    });

    socket.on('guess-made', (data: any) => {
      // Only animate if it's our own guess
      const isMyGuess = data.playerId === currentUserIdRef.current;
      
      if (isMyGuess) {
        // First, add the attempt to the game state so it appears in the grid
        setGameState(prev => {
          if (!prev) return prev;
          const newAttempt = {
            attemptNumber: data.attemptNumber,
            guess: data.guess,
            feedback: data.feedback,
            playerId: data.playerId
          };
          const updatedAttempts = [...prev.attempts, newAttempt];
          return {
            ...prev,
            attempts: updatedAttempts,
            status: data.status,
            currentTurn: data.currentTurn,
            winnerId: data.winnerId || null
          };
        });
        
        // Clear current guess before animation starts
        setCurrentGuess('');
        setSelectedCellIndex(0);
        
        // Set feedback for animation
        setAnimatingFeedback(data.feedback);
        
        // Start animation with feedback colors
        setIsAnimating(true);
        // Start with first cell immediately for smoother animation
        setAnimatingCellIndex(0);

        // Animate cells from left to right with feedback colors using requestAnimationFrame for smoother animation
        const animateCells = () => {
          let currentIndex = 1;
          const wordLength = gameState?.wordLength || 5;
          const cellDelay = 150; // Delay between cells for smooth animation
          let lastTime = performance.now();
          
          const animate = (currentTime: number) => {
            if (currentTime - lastTime >= cellDelay) {
              setAnimatingCellIndex(currentIndex);
              currentIndex++;
              lastTime = currentTime;
              
              if (currentIndex >= wordLength) {
                // Wait a bit after last cell, then finish animation
                setTimeout(() => {
                  setIsAnimating(false);
                  setAnimatingCellIndex(-1);
                  setAnimatingFeedback(null);
                  
                  // Update keyboard feedback after animation
                  setGameState(prev => {
                    if (!prev) return prev;
                    updateKeyboardFeedback(prev.attempts);
                    return prev;
                  });
                  
                  // Check if player lost (all attempts used without guessing) in race mode
                  if (gameMode === 'race' && !data.guessed && data.attemptNumber >= 6) {
                    // Player has used all 6 attempts without guessing - show lost modal
                    setTimeout(() => {
                      setShowRaceLostModal(true);
                    }, 500);
                  }
                  
                  if (data.guessed) {
                    if (gameMode === 'race') {
                      // In race mode, show winner modal for both players
                      setTimeout(() => {
                        if (gameState) {
                          previousLobbySettingsRef.current = {
                            wordLength: gameState.wordLength,
                            useUmlauts: gameState.useUmlauts
                          };
                        }
                        setShowRaceWinnerModal(true);
                        setRaceLoserChoice(null);
                        setShowRaceResult(false);
                        // Request rematch from opponent (for winner)
                        if (socket && gameState?.gameId && data.winnerId === currentUserIdRef.current) {
                          socket.emit('rematch-request', { gameId: gameState.gameId });
                        }
                      }, 500);
                    } else {
                      // Alternating mode - show rematch modal
                      setTimeout(() => {
                        // Store lobby settings for rematch
                        if (gameState) {
                          previousLobbySettingsRef.current = {
                            wordLength: gameState.wordLength,
                            useUmlauts: gameState.useUmlauts
                          };
                        }
                        setShowRematchModal(true);
                        setRematchStatus('pending');
                        // Request rematch from opponent
                        if (socket && gameState?.gameId) {
                          socket.emit('rematch-request', { gameId: gameState.gameId });
                        }
                      }, 500);
                    }
                  }
                }, 100);
              } else {
                requestAnimationFrame(animate);
              }
            } else {
              requestAnimationFrame(animate);
            }
          };
          
          requestAnimationFrame(animate);
        };

        animateCells();
        } else {
          // Not our guess - update immediately without animation
          setGameState(prev => {
            if (!prev) return prev;
            const newAttempt = {
              attemptNumber: data.attemptNumber,
              guess: data.guess,
              feedback: data.feedback,
              playerId: data.playerId
            };
            const updatedAttempts = [...prev.attempts, newAttempt];
            updateKeyboardFeedback(updatedAttempts);
            return {
              ...prev,
              attempts: updatedAttempts,
              status: data.status,
              currentTurn: data.currentTurn,
              winnerId: data.winnerId || null
            };
          });
          // Don't clear currentGuess when opponent makes a guess - only clear opponent's guess
          // Clear opponent guess after their attempt is submitted
          setOpponentCurrentGuess('');
        
          if (data.guessed) {
            if (gameMode === 'race') {
              // In race mode, show winner modal for both players
              if (data.winnerId) {
                setTimeout(() => {
                  if (gameState) {
                    previousLobbySettingsRef.current = {
                      wordLength: gameState.wordLength,
                      useUmlauts: gameState.useUmlauts
                    };
                  }
                  // Check if this is the loser who just guessed correctly
                  const isLoserWhoGuessed = data.winnerId !== currentUserIdRef.current && data.playerId === currentUserIdRef.current;
                  // If loser already chose to continue, and now finished (won or lost), show rematch modal
                  if (raceLoserChoice === 'continue' && data.winnerId !== currentUserIdRef.current) {
                    if (isLoserWhoGuessed) {
                      // Loser guessed correctly - show success modal with rematch option
                      setShowRaceWinnerModal(false);
                      setShowRematchModal(true);
                      setRematchStatus('pending');
                      if (socket && gameState?.gameId) {
                        socket.emit('rematch-request', { gameId: gameState.gameId });
                      }
                    } else {
                      // Loser finished without guessing - show rematch modal
                      setShowRaceWinnerModal(false);
                      setShowRematchModal(true);
                      setRematchStatus('pending');
                      if (socket && gameState?.gameId) {
                        socket.emit('rematch-request', { gameId: gameState.gameId });
                      }
                    }
                  } else {
                    // First win - show winner modal
                    setShowRaceWinnerModal(true);
                    setRaceLoserChoice(null);
                    setShowRaceResult(false);
                    // If we won, request rematch from opponent
                    if (data.winnerId === currentUserIdRef.current && socket && gameState?.gameId) {
                      socket.emit('rematch-request', { gameId: gameState.gameId });
                    }
                  }
                }, 500);
              }
            } else {
              // Alternating mode - show rematch modal
              setTimeout(() => {
                // Store lobby settings for rematch
                if (gameState) {
                  previousLobbySettingsRef.current = {
                    wordLength: gameState.wordLength,
                    useUmlauts: gameState.useUmlauts
                  };
                }
                setShowRematchModal(true);
                setRematchStatus('pending');
              }, 500);
            }
          }
        }
    });

    socket.on('turn-skipped', (data: any) => {
      setGameState(prev => prev ? { ...prev, currentTurn: data.currentTurn } : null);
      // Don't clear opponent guess - keep it visible so the other player can see the progress
      // The opponent's guess will be cleared when they start typing their own guess
    });

    socket.on('current-guess-update', (data: any) => {
      // Show opponent's guess in alternating mode and race mode
      if (gameType === 'multiplayer' && data.playerId !== currentUserIdRef.current) {
        if (gameMode === 'alternating' || gameMode === 'race') {
          // The currentGuess comes with spaces to preserve positions
          // Keep it as is (with spaces) so positions are preserved
          // Always update, even if empty, to show live typing
          const guessWithSpaces = data.currentGuess || '';
          const wordLength = gameState?.wordLength || 5;
          // Pad with spaces to wordLength to preserve positions
          let paddedGuess = guessWithSpaces;
          while (paddedGuess.length < wordLength) {
            paddedGuess += ' ';
          }
          setOpponentCurrentGuess(paddedGuess.slice(0, wordLength));
        }
      } else {
        // Clear opponent guess if it's our own update
        setOpponentCurrentGuess('');
      }
    });

    socket.on('race-word-validation', (data: any) => {
      if (data.valid) {
        if (!data.inList) {
          // Show modal asking if user wants to set word anyway
          setPendingRaceWord(data.word);
          setShowWordNotInListModal(true);
        } else {
          // Word is in list, set it directly
          if (socket && gameState) {
            socket.emit('confirm-race-word', { gameId: gameState.gameId, word: data.word });
          }
        }
      } else {
        setError(data.message || 'Ungültiges Wort');
        setTimeout(() => setError(''), 3000);
      }
    });

    socket.on('opponent-word-set', (data: any) => {
      // Show notification that opponent has set their word
      setGameState(prev => {
        if (!prev) return prev;
        const opponentUsername = prev.player1Id === currentUserIdRef.current 
          ? (prev.player2Username || 'Gegner')
          : (prev.player1Username || 'Gegner');
        // Use a separate state to avoid conflicts with error state
        const message = `${opponentUsername} hat das Wort gesetzt!`;
        setError(message);
        // Clear after 5 seconds to ensure it's visible
        setTimeout(() => {
          // Only clear if it's still the same message (not overwritten by another error)
          setError(prevError => prevError === message ? '' : prevError);
        }, 5000);
        // Update opponentWord in game state
        return {
          ...prev,
          opponentWord: data.word || prev.opponentWord
        };
      });
    });

    socket.on('race-started', (data: any) => {
      // Mark game as loaded
      gameLoadedRef.current = true;
      
      // Clear the timeout since game loaded successfully
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      
      // Update game state for race mode Phase 2
      setGameState(prev => prev ? {
        ...prev,
        racePhase: 'active' as const,
        myWord: data.myWord,
        opponentWord: data.opponentWord,
        raceStartTime: data.startTime,
        status: 'active'
      } : null);
      setLoading(false);
    });

    socket.on('race-word-confirmed', (data: any) => {
      // Word was confirmed and set - update game state
      setGameState(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          myWord: data.word
        };
      });
      // Clear currentGuess immediately to prevent it from being displayed
      setCurrentGuess('');
      setSelectedCellIndex(0);
    });

    socket.on('opponent-progress', (data: any) => {
      // Update opponent progress for race mode
      setGameState(prev => prev ? {
        ...prev,
        opponentProgress: {
          attemptNumber: data.attemptNumber,
          guessed: data.guessed
        }
      } : null);
    });

    socket.on('game-error', (data: { message: string }) => {
      setError(data.message);
      setTimeout(() => setError(''), 3000);
    });

    socket.on('opponent-left', (data: { message?: string }) => {
      const message = data.message || 'Der andere Spieler hat die Lobby verlassen. Das Spiel wurde beendet.';
      alert(message);
      navigate('/menu');
    });

    socket.on('rematch-request', (data: any) => {
      if (gameMode === 'alternating') {
        setOpponentRematchStatus('pending');
        // If we haven't shown the modal yet, show it now
        if (!showRematchModal) {
          setShowRematchModal(true);
          setRematchStatus('pending');
        }
      } else if (gameMode === 'race') {
        // In race mode, rematch request can come from winner or loser
        // If we're the winner and loser requested rematch, show new game modal
        const isWinner = gameState?.winnerId === currentUserIdRef.current;
        const isLoser = gameState?.winnerId && gameState.winnerId !== currentUserIdRef.current;
        
        if (isWinner && !showNewGameModal) {
          // Winner receives rematch request from loser - show new game modal
          setShowNewGameModal(true);
          setNewGameStatus(null);
          setOpponentNewGameStatus('pending');
        } else if (isLoser) {
          // Loser receives rematch request from winner
          setOpponentRematchStatus('pending');
        }
      }
    });

    socket.on('rematch-accepted', (data: any) => {
      if (gameMode === 'alternating') {
        setOpponentRematchStatus('accepted');
        // If we also accepted, create lobby (only the first one to accept after both accepted will create it)
        if (rematchStatus === 'accepted') {
          // Small delay to ensure state is updated
          setTimeout(() => {
            handleRematchCreateLobby();
          }, 100);
        }
      } else if (gameMode === 'race') {
        setOpponentRematchStatus('accepted');
        // Close the modal if it's still open
        setShowRaceWinnerModal(false);
        // If we also accepted, create lobby
        if (rematchStatus === 'accepted') {
          setTimeout(() => {
            handleRematchCreateLobby();
          }, 100);
        }
      }
    });

    socket.on('rematch-declined', (data: any) => {
      if (gameMode === 'alternating' || gameMode === 'race') {
        setOpponentRematchStatus('declined');
        const opponentUsername = gameState?.player1Id === currentUserIdRef.current 
          ? (gameState.player2Username || 'Der andere Spieler')
          : (gameState?.player1Username || 'Der andere Spieler');
        alert(`${opponentUsername} möchte nicht nochmal spielen.`);
        setTimeout(() => {
          navigate('/menu');
        }, 2000);
      }
    });

    socket.on('rematch-lobby-created', (data: any) => {
      if (gameMode === 'alternating' || gameMode === 'race') {
        navigate(`/lobby?lobbyId=${data.lobbyId}`);
      }
    });

    socket.on('new-game-lobby-created', (data: any) => {
      if (gameType === 'multiplayer') {
        navigate(`/lobby?lobbyId=${data.lobbyId}`);
      }
    });

    socket.on('new-game-request', (data: any) => {
      if (gameType === 'multiplayer') {
        // Don't open new game modal if loser has race winner modal open or is showing result
        const isLoser = gameMode === 'race' && gameState?.winnerId && gameState.winnerId !== currentUserIdRef.current;
        const hasRaceWinnerModalOpen = isLoser && (showRaceWinnerModal || (raceLoserChoice === 'show-result' && showRaceResult));
        
        // Only show modal if it's not already open AND loser doesn't have race winner modal open
        // Keep the race winner modal open for the loser so they can make their choice
        if (!showNewGameModal && !hasRaceWinnerModalOpen) {
          setShowNewGameModal(true);
        }
        // Don't reset status if loser has modal open - let them finish their choice
        if (!hasRaceWinnerModalOpen) {
          setNewGameStatus(null); // Reset own status
        }
        setOpponentNewGameStatus('pending');
      }
    });

    socket.on('new-game-accepted', (data: any) => {
      if (gameType === 'multiplayer') {
        setOpponentNewGameStatus('accepted');
        // Navigation will happen when both have accepted (handled in useEffect)
      }
    });

    socket.on('new-game-declined', (data: any) => {
      if (gameType === 'multiplayer') {
        setOpponentNewGameStatus('declined');
        // Don't close modal immediately - let user see the message
        // setShowNewGameModal(false);
        // Show message in modal instead of alert
      }
    });

    return () => {
      socket.off('game-started');
      socket.off('game-state');
      socket.off('guess-made');
      socket.off('turn-skipped');
      socket.off('game-error');
      socket.off('opponent-left');
      socket.off('rematch-request');
      socket.off('rematch-accepted');
      socket.off('rematch-declined');
      socket.off('rematch-lobby-created');
      socket.off('new-game-request');
      socket.off('new-game-accepted');
      socket.off('new-game-declined');
      socket.off('new-game-lobby-created');
      // Clear timeout on cleanup
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };
  }, [socket, gameType, navigate, gameIdParam]);

  // Invitations are now handled globally via InvitationContext

  // Update timer for daily wordle
  useEffect(() => {
    if (gameType !== 'solo' || gameMode !== 'daily' || !gameState?.nextResetTime) {
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const resetTime = new Date(gameState.nextResetTime!);
      const diff = resetTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeUntilNextDaily('00:00:00');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeUntilNextDaily(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      );
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [gameType, gameMode, gameState?.nextResetTime]);

  const loadSoloGame = async () => {
    try {
      const response = await fetch(`${API_URL}/games/solo/current?gameType=${gameMode}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.game) {
        setGameState({
          ...data.game,
          targetWord: data.game.targetWord // Store target word for solution display
        });
        updateKeyboardFeedback(data.game.attempts);
        // Show solution if game is lost
        if (data.game.status === 'lost') {
          setShowSolution(false); // Reset, will be shown when button is clicked
        }
      } else {
        await createSoloGame();
      }
    } catch (err) {
      console.error('Error loading game:', err);
      setError('Fehler beim Laden des Spiels');
    } finally {
      setLoading(false);
    }
  };

  const loadMultiplayerGame = (gameId: number) => {
    if (!socket) {
      setError('Verbindung zum Server fehlt');
      setLoading(false);
      return;
    }
    
    // Reset game loaded flag
    gameLoadedRef.current = false;
    
    // Clear any existing timeout
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    
    console.log('Joining multiplayer game:', gameId);
    socket.emit('join-game', { gameId });
    
    // Set a timeout to show error if game doesn't load
    loadTimeoutRef.current = setTimeout(() => {
      // Only show error if game hasn't loaded yet
      if (!gameLoadedRef.current) {
        setError('Spiel konnte nicht geladen werden. Bitte versuche es erneut.');
        setLoading(false);
      }
      loadTimeoutRef.current = null;
    }, 5000);
  };

  const createSoloGame = async () => {
    try {
      const response = await fetch(`${API_URL}/games/solo/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ gameType: gameMode }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // If game already existed, reload it to get all attempts
        if (data.existing) {
          // Reload the game to get all attempts
          await loadSoloGame();
        } else {
          // Wait a bit for the database to be ready, then load the game
          setTimeout(async () => {
            const gameResponse = await fetch(`${API_URL}/games/solo/current?gameType=${gameMode}`, {
              credentials: 'include'
            });
            const gameData = await gameResponse.json();
            
            if (gameData.game) {
              setGameState({
                ...gameData.game,
                targetWord: gameData.game.targetWord
              });
              updateKeyboardFeedback(gameData.game.attempts || []);
            } else {
              setError('Fehler beim Laden des erstellten Spiels');
            }
            setLoading(false);
          }, 100);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Fehler beim Erstellen des Spiels');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error creating game:', err);
      setError('Fehler beim Erstellen des Spiels');
      setLoading(false);
    }
  };

  const updateKeyboardFeedback = (attempts: Array<{ guess: string; feedback: string[]; playerId?: number }>) => {
    const feedback: { [key: string]: 'green' | 'yellow' | 'gray' } = {};
    
    // In race mode, only use own attempts for keyboard feedback
    const attemptsToUse = gameType === 'multiplayer' && gameMode === 'race'
      ? attempts.filter(attempt => attempt.playerId === currentUserIdRef.current)
      : attempts;
    
    attemptsToUse.forEach(attempt => {
      attempt.guess.split('').forEach((letter, index) => {
        const upperLetter = letter.toUpperCase();
        const currentFeedback = attempt.feedback[index];
        
        if (!feedback[upperLetter] || 
            (currentFeedback === 'green') ||
            (currentFeedback === 'yellow' && feedback[upperLetter] !== 'green')) {
          feedback[upperLetter] = currentFeedback as 'green' | 'yellow' | 'gray';
        }
      });
    });
    
    setKeyboardFeedback(feedback);
  };

  const handleKeyPress = (key: string) => {
    if (!gameState) return;
    
    // Block winner from entering more words in race mode
    if (gameType === 'multiplayer' && gameMode === 'race' && gameState.winnerId === currentUserIdRef.current) {
      return; // Winner cannot enter more words
    }
    
    // In race mode, allow loser to continue even if status is 'won'
    const isRaceLoserContinuing = gameType === 'multiplayer' && gameMode === 'race' && gameState.winnerId !== currentUserIdRef.current && raceLoserChoice === 'continue';
    if (gameState.status !== 'active' && !isRaceLoserContinuing) {
      setError('Das Spiel ist nicht aktiv');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    // Don't allow input during animation
    if (isAnimating) return;
    
    // For multiplayer alternating mode, check if it's your turn
    // Race mode allows both players to play simultaneously
    if (gameType === 'multiplayer' && gameMode === 'alternating' && gameState.currentTurn !== currentUserIdRef.current) {
      setError('Nicht dein Zug');
      return;
    }
    
    // Clear opponent guess when starting to type (in alternating mode and race mode)
    if (gameType === 'multiplayer' && (gameMode === 'alternating' || gameMode === 'race') && opponentCurrentGuess) {
      setOpponentCurrentGuess('');
    }
    
    // Ensure selectedCellIndex is valid, default to first empty cell or end
    let currentSelectedIndex = selectedCellIndex;
    if (currentSelectedIndex < 0 || currentSelectedIndex >= gameState.wordLength) {
      const guessLength = getLogicalLength(currentGuess.replace(/\s/g, ''));
      currentSelectedIndex = Math.min(guessLength, gameState.wordLength - 1);
      setSelectedCellIndex(currentSelectedIndex);
    }
    
    // Insert letter at selected position
    if (currentSelectedIndex >= 0 && currentSelectedIndex < gameState.wordLength) {
      // Build array with fixed length, using spaces to preserve positions
      const guessArray: string[] = new Array(gameState.wordLength).fill(' ');
      
      // Fill existing letters from currentGuess (ignoring spaces)
      const currentGuessClean = currentGuess.replace(/\s/g, '');
      const currentGuessLogicalLength = getLogicalLength(currentGuessClean);
      for (let i = 0; i < currentGuessLogicalLength && i < gameState.wordLength; i++) {
        // Find the original position in currentGuess (accounting for spaces)
        let originalPos = 0;
        let cleanIndex = 0;
        while (cleanIndex < i && originalPos < currentGuess.length) {
          if (currentGuess[originalPos] !== ' ') {
            cleanIndex++;
          }
          originalPos++;
        }
        // Now find where this letter actually is in the spaced version
        // Actually, simpler: just place letters in order, but we need to track which positions are filled
        // Let's use a different approach: store currentGuess with spaces to preserve positions
      }
      
      // Actually, simpler approach: always maintain currentGuess with spaces for empty positions
      // Pad currentGuess to wordLength with spaces
      let paddedGuess = currentGuess;
      while (paddedGuess.length < gameState.wordLength) {
        paddedGuess += ' ';
      }
      // Convert to array
      const guessArray2 = paddedGuess.split('').slice(0, gameState.wordLength);
      
      // Set letter at selected position
      guessArray2[currentSelectedIndex] = key;
      
      // Join back, but trim trailing spaces
      let newGuess = guessArray2.join('').replace(/\s+$/, '');
      
      setCurrentGuess(newGuess);
      
      // Move to next cell if available (user liked this feature)
      if (currentSelectedIndex < gameState.wordLength - 1) {
        setSelectedCellIndex(currentSelectedIndex + 1);
      }
      
      // Broadcast current guess update for alternating mode and race mode
      // Send with spaces to preserve positions (spaces are placeholders for empty cells)
      if (gameType === 'multiplayer' && (gameMode === 'alternating' || gameMode === 'race') && socket && gameState?.gameId) {
        // Pad to wordLength with spaces to preserve all positions
        let paddedGuessForBroadcast = newGuess;
        while (paddedGuessForBroadcast.length < gameState.wordLength) {
          paddedGuessForBroadcast += ' ';
        }
        socket.emit('current-guess-update', {
          gameId: gameState.gameId,
          currentGuess: paddedGuessForBroadcast.slice(0, gameState.wordLength) // Keep spaces for position preservation
        });
      }
      
      setError('');
    }
  };

  const handleCellClick = (index: number) => {
    if (!gameState) return;
    
    // Block winner from entering more words in race mode
    if (gameType === 'multiplayer' && gameMode === 'race' && gameState.winnerId === currentUserIdRef.current) {
      return; // Winner cannot enter more words
    }
    
    // In race mode, allow loser to continue even if status is 'won'
    const isRaceLoserContinuing = gameType === 'multiplayer' && gameMode === 'race' && gameState.winnerId !== currentUserIdRef.current && raceLoserChoice === 'continue';
    if (gameState.status !== 'active' && !isRaceLoserContinuing) {
      setError('Das Spiel ist nicht aktiv');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (isAnimating) return;
    
    // For multiplayer alternating mode, check if it's your turn
    // Race mode allows both players to play simultaneously
    if (gameType === 'multiplayer' && gameMode === 'alternating' && gameState.currentTurn !== currentUserIdRef.current) {
      return;
    }
    
    // Set selected cell index
    if (index >= 0 && index < gameState.wordLength) {
      setSelectedCellIndex(index);
    }
  };

  const handleBackspace = () => {
    // Don't allow input during animation
    if (isAnimating) return;
    if (!gameState) return;
    
    if (currentGuess.length === 0) {
      return; // Nothing to delete
    }
    
    // Pad currentGuess to wordLength with spaces if needed
    let paddedGuess = currentGuess;
    while (paddedGuess.length < gameState.wordLength) {
      paddedGuess += ' ';
    }
    const guessArray = paddedGuess.split('').slice(0, gameState.wordLength);
    
    let newGuess = '';
    // If a cell is selected, delete the letter at that position (or previous if selectedCellIndex is at the end)
    if (selectedCellIndex >= 0 && selectedCellIndex < currentGuess.length) {
      // Delete letter at selected position
      guessArray[selectedCellIndex] = ' ';
      // Join and trim trailing spaces
      newGuess = guessArray.join('').replace(/\s+$/, '');
      setCurrentGuess(newGuess);
      // Move selection to previous position, or stay at current if at start
      if (selectedCellIndex > 0) {
        setSelectedCellIndex(selectedCellIndex - 1);
      }
    } else if (selectedCellIndex >= currentGuess.length) {
      // Selected cell is after the last letter - delete the last letter
      const lastIndex = currentGuess.length - 1;
      guessArray[lastIndex] = ' ';
      newGuess = guessArray.join('').replace(/\s+$/, '');
      setCurrentGuess(newGuess);
      setSelectedCellIndex(lastIndex);
    } else {
      // No specific cell selected or at start - standard backspace behavior
      newGuess = currentGuess.slice(0, -1);
      setCurrentGuess(newGuess);
      if (selectedCellIndex > 0) {
        setSelectedCellIndex(selectedCellIndex - 1);
      } else {
        setSelectedCellIndex(0);
      }
    }
    
    // Broadcast current guess update for alternating mode and race mode
    // Send with spaces to preserve positions (spaces are placeholders for empty cells)
    if (gameType === 'multiplayer' && (gameMode === 'alternating' || gameMode === 'race') && socket && gameState?.gameId) {
      // Pad to wordLength with spaces to preserve all positions
      let paddedGuessForBroadcast = newGuess;
      while (paddedGuessForBroadcast.length < gameState.wordLength) {
        paddedGuessForBroadcast += ' ';
      }
      socket.emit('current-guess-update', {
        gameId: gameState.gameId,
        currentGuess: paddedGuessForBroadcast.slice(0, gameState.wordLength) // Keep spaces for position preservation
      });
    }
    
    setError('');
  };

  const handleEnter = async () => {
    if (!gameState || isSubmitting) return;
    
    // Block winner from entering more words in race mode
    if (gameType === 'multiplayer' && gameMode === 'race' && gameState.winnerId === currentUserIdRef.current) {
      return; // Winner cannot enter more words
    }
    
    // In race mode, allow loser to continue even if status is 'won'
    const isRaceLoserContinuing = gameType === 'multiplayer' && gameMode === 'race' && gameState.winnerId !== currentUserIdRef.current && raceLoserChoice === 'continue';
    if (gameState.status !== 'active' && !isRaceLoserContinuing) {
      setError('Das Spiel ist nicht aktiv');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    // For multiplayer alternating mode, check if it's your turn
    if (gameType === 'multiplayer' && gameMode === 'alternating' && gameState.currentTurn !== currentUserIdRef.current) {
      setError('Nicht dein Zug');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    // Remove spaces before checking length (spaces are placeholders for empty positions)
    const guessWithoutSpaces = currentGuess.replace(/\s/g, '');
    if (getLogicalLength(guessWithoutSpaces) !== gameState.wordLength) {
      setError(`Das Wort muss ${gameState.wordLength} Buchstaben lang sein`);
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Send guess first to get feedback, then animate with colors
    if (gameType === 'solo') {
      handleSoloGuessWithAnimation();
    } else {
      handleMultiplayerGuessWithAnimation();
    }
  };

  const handleSoloGuessWithAnimation = async () => {
    if (!gameState) return;

    try {
      const response = await fetch(`${API_URL}/games/solo/guess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          gameId: gameState.gameId,
          guess: normalizeSharpS(currentGuess.replace(/\s/g, '')).toUpperCase() // Normalize ß before uppercasing
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // First, add the attempt to the game state so it appears in the grid
        const newAttempt = {
          attemptNumber: data.attemptNumber,
          guess: normalizeSharpS(currentGuess.replace(/\s/g, '')).toUpperCase(), // Normalize ß before uppercasing
          feedback: data.feedback
        };
        
        const updatedAttempts = [...(gameState.attempts || []), newAttempt];
        const updatedState = {
          ...gameState,
          attempts: updatedAttempts,
          status: data.status,
          targetWord: data.targetWord || gameState.targetWord
        };
        
        setGameState(updatedState);
        
        // Clear current guess before animation starts
        setCurrentGuess('');
        setSelectedCellIndex(0);
        
        // Set feedback for animation
        setAnimatingFeedback(data.feedback);
        
        // Start animation with feedback colors
        setIsAnimating(true);
        // Start with first cell immediately for smoother animation
        setAnimatingCellIndex(0);

        // Animate cells from left to right with feedback colors using requestAnimationFrame for smoother animation
        const animateCells = () => {
          let currentIndex = 1;
          const wordLength = gameState.wordLength;
          const cellDelay = 150; // Delay between cells for smooth animation
          let lastTime = performance.now();
          
          const animate = (currentTime: number) => {
            if (currentTime - lastTime >= cellDelay) {
              setAnimatingCellIndex(currentIndex);
              currentIndex++;
              lastTime = currentTime;
              
              if (currentIndex >= wordLength) {
                // Wait a bit after last cell, then finish animation
                setTimeout(() => {
                  setIsAnimating(false);
                  setAnimatingCellIndex(-1);
                  setAnimatingFeedback(null);
                  
                  // Update keyboard feedback after animation
                  updateKeyboardFeedback(updatedAttempts);
                  
                  if (data.guessed) {
                    // Only show remove word dialog for free mode, not daily
                    setTimeout(() => {
                      if (gameMode === 'free') {
                        const removeWord = window.confirm(
                          'Glückwunsch! Du hast das Wort erraten!\n\nMöchtest du dieses Wort aus deiner persönlichen Wörterliste entfernen?'
                        );
                        if (removeWord) {
                          handleRemoveWord(normalizeSharpS(currentGuess.replace(/\s/g, '')).toUpperCase(), gameState.wordLength, gameState.useUmlauts);
                        }
                        
                        if (window.confirm('Möchtest du ein neues Spiel starten?')) {
                          createSoloGame();
                        } else {
                          navigate('/menu');
                        }
                      } else {
                        // Daily mode: stay on result screen
                      }
                    }, 500);
                  } else if (data.status === 'lost') {
                    // Don't auto-show solution, wait for button click
                    setShowSolution(false);
                  }
                }, 100);
              } else {
                requestAnimationFrame(animate);
              }
            } else {
              requestAnimationFrame(animate);
            }
          };
          
          requestAnimationFrame(animate);
        };

        animateCells();
      } else {
        // Check if we can add this word
        if (data.canAdd && data.word) {
          setWordToAdd({
            word: data.word,
            wordLength: data.wordLength,
            useUmlauts: data.useUmlauts
          });
          setShowWordNotFoundModal(true);
        } else {
          setError(data.error || 'Ungültiges Wort');
          setTimeout(() => setError(''), 3000);
        }
      }
    } catch (err) {
      setError('Fehler beim Verarbeiten des Rateversuchs. Bitte versuchen Sie es erneut.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleSoloGuess = async () => {
    if (!gameState) return;

    try {
      const response = await fetch(`${API_URL}/games/solo/guess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          gameId: gameState.gameId,
          guess: currentGuess.replace(/\s/g, '').toUpperCase() // Remove spaces before sending
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const newAttempt = {
          attemptNumber: data.attemptNumber,
          guess: normalizeSharpS(currentGuess.replace(/\s/g, '')).toUpperCase(), // Normalize ß before uppercasing
          feedback: data.feedback
        };
        
        const updatedAttempts = [...(gameState.attempts || []), newAttempt];
        const updatedState = {
          ...gameState,
          attempts: updatedAttempts,
          status: data.status,
          targetWord: data.targetWord || gameState.targetWord
        };
        
        setGameState(updatedState);
        updateKeyboardFeedback(updatedAttempts);
        setCurrentGuess('');
        setSelectedCellIndex(0); // Reset selection after guess
        
        if (data.guessed) {
          // Only show remove word dialog for free mode, not daily
          setTimeout(() => {
            if (gameMode === 'free') {
              const removeWord = window.confirm(
                'Glückwunsch! Du hast das Wort erraten!\n\nMöchtest du dieses Wort aus deiner persönlichen Wörterliste entfernen?'
              );
              if (removeWord) {
                handleRemoveWord(normalizeSharpS(currentGuess.replace(/\s/g, '')).toUpperCase(), gameState.wordLength, gameState.useUmlauts);
              }
              
              if (window.confirm('Möchtest du ein neues Spiel starten?')) {
                createSoloGame();
              } else {
                navigate('/menu');
              }
            } else {
              // Daily mode: stay on result screen
            }
          }, 500);
        } else if (data.status === 'lost') {
          // Don't auto-show solution, wait for button click
          setShowSolution(false);
        }
      } else {
        // Check if we can add this word
        if (data.canAdd && data.word) {
          setWordToAdd({
            word: data.word,
            wordLength: data.wordLength,
            useUmlauts: data.useUmlauts
          });
          setShowWordNotFoundModal(true);
        } else {
          setError(data.error || 'Ungültiges Wort');
          setTimeout(() => setError(''), 3000);
        }
      }
    } catch (err) {
      setError('Fehler beim Verarbeiten des Rateversuchs. Bitte versuchen Sie es erneut.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleMultiplayerGuessWithAnimation = () => {
    if (!socket || !gameState || isSubmitting) return;

    // Prevent double submission
    setIsSubmitting(true);

    // Don't clear opponent guess - let it stay visible
    // setOpponentCurrentGuess(''); // Removed - keep opponent's guess visible

    // Store the guess temporarily for animation
    const guessToSend = currentGuess;

    // For multiplayer, we need to wait for the feedback from the server
    // The animation will be triggered by the 'guess-made' event handler
    socket.emit('make-guess', {
      gameId: gameState.gameId,
      guess: guessToSend
    });
    
    // Reset submitting flag after delay to prevent double submission
    setTimeout(() => {
      setIsSubmitting(false);
    }, 500);
    
    // Note: currentGuess will be cleared after animation completes in the 'guess-made' handler
  };

  const handleMultiplayerGuess = () => {
    if (!socket || !gameState || isSubmitting) return;

    // Prevent double submission
    setIsSubmitting(true);

    // Don't clear opponent guess immediately - let it clear naturally when they start typing
    // setOpponentCurrentGuess(''); // Removed - let opponent's guess stay visible until they start typing

    socket.emit('make-guess', {
      gameId: gameState.gameId,
      guess: normalizeSharpS(currentGuess.replace(/\s/g, '')).toUpperCase() // Normalize ß before uppercasing
    });

    // Reset submitting flag after delay to prevent double submission
    setTimeout(() => {
      setIsSubmitting(false);
    }, 500);
  };

  const handleSetRaceWord = () => {
    const guessWithoutSpaces = currentGuess.replace(/\s/g, '');
    if (!socket || !gameState || getLogicalLength(guessWithoutSpaces) !== gameState.wordLength) {
      setError(`Das Wort muss ${gameState?.wordLength} Buchstaben lang sein`);
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Check if word is already set - if so, don't allow setting again
    if (gameState.myWord) {
      setError('Du hast bereits ein Wort gesetzt.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    socket.emit('set-race-word', {
      gameId: gameState.gameId,
      word: normalizeSharpS(currentGuess.replace(/\s/g, '')).toUpperCase() // Normalize ß before uppercasing
    });
    // Clear currentGuess immediately to prevent it from being displayed in the input field
    setCurrentGuess('');
    setSelectedCellIndex(0);
  };

  const handleConfirmRaceWord = () => {
    if (!socket || !gameState || !pendingRaceWord) return;
    socket.emit('confirm-race-word', {
      gameId: gameState.gameId,
      word: pendingRaceWord
    });
    setShowWordNotInListModal(false);
    setPendingRaceWord(null);
    setCurrentGuess('');
    setSelectedCellIndex(0);
  };

  const handleCancelRaceWord = () => {
    setShowWordNotInListModal(false);
    setPendingRaceWord(null);
    // Keep currentGuess so user can edit it
  };

  const handleSkipTurn = () => {
    if (!socket || !gameState) return;
    
    if (gameState.currentTurn !== currentUserIdRef.current) {
      setError('Nicht dein Zug');
      return;
    }

    socket.emit('skip-turn', { gameId: gameState.gameId });
    setError('');
  };

  const handleRematchAccept = () => {
    if (!socket || !gameState) return;
    setRematchStatus('accepted');
    socket.emit('rematch-accept', { gameId: gameState.gameId });
    
    // If opponent already accepted, create lobby
    if (opponentRematchStatus === 'accepted') {
      handleRematchCreateLobby();
    }
  };

  const handleRematchDecline = () => {
    if (!socket || !gameState) return;
    setRematchStatus('declined');
    socket.emit('rematch-decline', { gameId: gameState.gameId });
    setTimeout(() => {
      navigate('/menu');
    }, 1000);
  };

  const handleRematchCreateLobby = () => {
    if (!socket || !gameState || !previousLobbySettingsRef.current) return;
    
    // Create lobby with previous settings
    socket.emit('create-rematch-lobby', {
      gameId: gameState.gameId,
      wordLength: previousLobbySettingsRef.current.wordLength,
      useUmlauts: previousLobbySettingsRef.current.useUmlauts
    });
  };

  const handleNewGame = () => {
    if (gameMode === 'free') {
      createSoloGame();
    } else {
      navigate('/menu');
    }
  };

  const handleRaceLoserContinue = () => {
    setRaceLoserChoice('continue');
    setShowRaceWinnerModal(false);
    // Loser can continue playing
  };

  const handleRaceLoserShowResult = () => {
    setRaceLoserChoice('show-result');
    setShowRaceResult(true);
    // Keep modal open to show result
    // setShowRaceWinnerModal(false);
  };

  const handleRaceLoserRematch = () => {
    if (!socket || !gameState) return;
    // Send rematch request to winner so they get a pop-up
    socket.emit('rematch-request', { gameId: gameState.gameId });
    setRematchStatus('pending');
    // Don't close modal - wait for winner's response
    // setShowRaceWinnerModal(false);
  };

  const handleNewGameRequest = () => {
    if (!socket || !gameState) return;
    // Store current settings for new lobby
    previousLobbySettingsRef.current = {
      wordLength: gameState.wordLength,
      useUmlauts: gameState.useUmlauts
    };
    setShowNewGameModal(true);
    setNewGameStatus('pending');
    setOpponentNewGameStatus(null);
    socket.emit('new-game-request', { gameId: gameState.gameId });
  };

  const handleNewGameAccept = () => {
    if (!socket || !gameState) return;
    setNewGameStatus('accepted');
    socket.emit('new-game-accept', { gameId: gameState.gameId });
    // Navigation will happen when both have accepted (handled in useEffect)
  };

  const handleNewGameDecline = () => {
    if (!socket || !gameState) return;
    setNewGameStatus('declined');
    setShowNewGameModal(false);
    socket.emit('new-game-decline', { gameId: gameState.gameId });
  };

  const handleAddWord = async () => {
    if (!wordToAdd) return;
    
    try {
      const response = await fetch(`${API_URL}/user-words/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(wordToAdd),
      });
      
      if (response.ok) {
        setShowWordNotFoundModal(false);
        const word = wordToAdd.word;
        setWordToAdd(null);
        // Add word to list WITHOUT consuming an attempt
        // Keep the word in currentGuess so the player can submit it manually
        setCurrentGuess(normalizeSharpS(word).toUpperCase());
        // Don't automatically submit - let the player decide
        setError('Wort wurde zur Liste hinzugefügt. Du kannst es jetzt versuchen.');
        setTimeout(() => setError(''), 3000);
      } else {
        setError('Fehler beim Hinzufügen des Wortes');
        setShowWordNotFoundModal(false);
      }
    } catch (err) {
      setError('Fehler beim Hinzufügen des Wortes');
      setShowWordNotFoundModal(false);
    }
  };

  const handleRemoveWord = async (word: string, wordLength: number, useUmlauts: boolean) => {
    try {
      await fetch(`${API_URL}/user-words/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ word, wordLength, useUmlauts }),
      });
    } catch (err) {
      console.error('Error removing word:', err);
    }
  };


  // Create new lobby when both players have accepted new game
  useEffect(() => {
    if (gameType === 'multiplayer' && newGameStatus === 'accepted' && opponentNewGameStatus === 'accepted' && gameState) {
      // Create new lobby with same settings
      if (socket && previousLobbySettingsRef.current) {
        socket.emit('create-new-game-lobby', {
          gameId: gameState.gameId,
          wordLength: previousLobbySettingsRef.current.wordLength || gameState.wordLength,
          useUmlauts: previousLobbySettingsRef.current.useUmlauts !== undefined ? previousLobbySettingsRef.current.useUmlauts : gameState.useUmlauts,
          gameMode: gameMode
        });
      }
    }
  }, [newGameStatus, opponentNewGameStatus, gameType, gameState, socket, gameMode]);

  const handleShowSolution = async () => {
    if (!gameState) return;
    
    try {
      const response = await fetch(`${API_URL}/games/solo/current?gameType=${gameMode}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.game && data.game.targetWord) {
        // Update game state with target word and show it
        setGameState(prev => prev ? { ...prev, targetWord: data.game.targetWord } : null);
        setShowSolution(true);
      } else if (gameState.targetWord) {
        // If target word is already in state, just show it
        setShowSolution(true);
      }
    } catch (err) {
      console.error('Error getting solution:', err);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-4">
        <div className="max-w-2xl mx-auto pt-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
            <div className="text-center text-gray-800 text-xl">Lädt...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-4">
        <div className="max-w-2xl mx-auto pt-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 relative">
            <div className="flex justify-end mb-6">
              <button
                onClick={() => navigate('/menu')}
                className="border-2 border-blue-500 hover:border-blue-700 bg-white hover:bg-blue-50 text-blue-600 hover:text-blue-700 font-semibold py-2 px-4 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Menü
              </button>
            </div>
            <div className="text-center">
              <p className="text-gray-800 mb-4">Spiel wird erstellt...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isMyTurn = gameType === 'multiplayer' 
    ? (gameMode === 'race' ? true : gameState.currentTurn === currentUserIdRef.current)
    : true;
  const canSkip = gameType === 'multiplayer' && isMyTurn && gameState.status === 'active' && gameMode === 'alternating';
  
  // Race mode: Phase 1 (word selection) or Phase 2 (guessing)
  const isRacePhase1 = gameType === 'multiplayer' && gameMode === 'race' && gameState.racePhase === 'word_selection';
  const isRacePhase2 = gameType === 'multiplayer' && gameMode === 'race' && gameState.racePhase === 'active';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-2 sm:p-4 overflow-x-hidden">
      <div className="max-w-2xl mx-auto pt-2 sm:pt-4">
        <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 relative">
          <div className="flex justify-between items-center mb-4 sm:mb-6 gap-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 flex-1 min-w-0">
              {gameType === 'solo' 
                ? (gameMode === 'daily' ? 'Tägliches Frördle' : 'Freies Frördlen')
                : (gameMode === 'alternating' ? 'Gemeinsames Frördle' : '1 vs 1 Frördle')
              }
            </h1>
            <button
              onClick={() => navigate('/menu')}
              className="border-2 border-blue-500 hover:border-blue-700 bg-white hover:bg-blue-50 text-blue-600 hover:text-blue-700 font-semibold py-2 px-3 sm:px-4 rounded-lg transition duration-200 text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0"
            >
              Menü
            </button>
          </div>
          
          <div className="mb-4 sm:mb-6">
            {gameType === 'multiplayer' && (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleNewGameRequest}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 sm:py-2 px-4 rounded-lg transition duration-200 text-sm sm:text-base min-h-[44px]"
                >
                  Neues Spiel
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Möchtest du die Lobby wirklich verlassen? Das Spiel wird dadurch beendet.')) {
                      if (socket && gameState) {
                        socket.emit('leave-game', { gameId: gameState.gameId });
                      }
                      navigate('/menu');
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 sm:py-2 px-4 rounded-lg transition duration-200 text-sm sm:text-base min-h-[44px]"
                >
                  Lobby verlassen
                </button>
              </div>
            )}
          </div>

          {/* Race Mode Phase 1: Word Selection */}
          {isRacePhase1 && !gameState.myWord && (
            <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
              <h2 className="text-lg font-bold text-gray-800 mb-2">Phase 1: Wort eingeben</h2>
              <p className="text-sm text-gray-600 mb-4">
                Gib ein Wort ein, das {gameState.player1Id === currentUserIdRef.current 
                  ? (gameState.player2Username || 'dein Gegner')
                  : (gameState.player1Username || 'dein Gegner')} erraten soll. Das Wort muss {gameState.wordLength} Buchstaben lang sein.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={currentGuess}
                  onChange={(e) => {
                    const normalized = normalizeSharpS(e.target.value);
                    const val = normalized.toUpperCase().slice(0, gameState.wordLength);
                    setCurrentGuess(val);
                    setSelectedCellIndex(getLogicalLength(val.replace(/\s/g, '')));
                  }}
                  className="flex-1 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-center text-base sm:text-xl font-semibold uppercase min-h-[44px]"
                  maxLength={gameState.wordLength}
                  placeholder="Wort eingeben"
                />
                <button
                  onClick={handleSetRaceWord}
                  disabled={getLogicalLength(currentGuess.replace(/\s/g, '')) !== gameState.wordLength}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-2.5 sm:py-2 px-4 sm:px-6 rounded-lg transition duration-200 min-h-[44px] whitespace-nowrap"
                >
                  Wort setzen
                </button>
              </div>
              {error && error.includes('hat das Wort gesetzt') && (
                <p className="text-green-600 font-semibold mt-2">✓ {gameState.player1Id === currentUserIdRef.current 
                  ? (gameState.player2Username || 'Gegner')
                  : (gameState.player1Username || 'Gegner')} hat das Wort gesetzt!</p>
              )}
            </div>
          )}
          {isRacePhase1 && gameState.myWord && (
            <div className="mb-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
              <p className="text-green-700 font-semibold">✓ Du hast dein Wort gesetzt: <span className="text-xl">{gameState.myWord}</span></p>
              {!gameState.opponentWord && (
                <p className="text-gray-600 mt-2 text-sm">Warten auf {gameState.player1Id === currentUserIdRef.current 
                  ? (gameState.player2Username || 'Gegner')
                  : (gameState.player1Username || 'Gegner')}...</p>
              )}
            </div>
          )}

          {/* Race Mode Phase 2: Timer and Game */}
          {isRacePhase2 && (
            <div className="mb-4 p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
              <div className="flex justify-end items-center mb-2">
                <button
                  onClick={() => setShowOpponentProgress(!showOpponentProgress)}
                  className="bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-lg transition duration-200"
                  title={`${gameState.player1Id === currentUserIdRef.current 
                    ? (gameState.player2Username || 'Gegner')
                    : (gameState.player1Username || 'Gegner')}-Fortschritt anzeigen`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              {showOpponentProgress && (
                <div className="mt-4 p-4 bg-white rounded-lg border-2 border-purple-300 shadow-lg">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold text-gray-800">{gameState.player1Id === currentUserIdRef.current 
                      ? (gameState.player2Username || 'Gegner')
                      : (gameState.player1Username || 'Gegner')}-Fortschritt</h3>
                    <button
                      onClick={() => setShowOpponentProgress(false)}
                      className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                    >
                      ×
                    </button>
                  </div>
                  {gameState.opponentProgress && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600">
                        Versuch {gameState.opponentProgress.attemptNumber}/{gameMode === 'race' ? 6 : 5}
                        {gameState.opponentProgress.guessed && <span className="text-green-600 font-bold ml-2">✓ Erraten!</span>}
                      </p>
                    </div>
                  )}
                  {/* Show opponent's attempts - always show grid, even if empty */}
                  <div className="mt-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">{gameState.player1Id === currentUserIdRef.current 
                      ? (gameState.player2Username || 'Gegner')
                      : (gameState.player1Username || 'Gegner')}-Versuche:</p>
                    <WordleGrid
                      wordLength={gameState.wordLength}
                      attempts={gameState.attempts ? gameState.attempts.filter(attempt => attempt.playerId !== currentUserIdRef.current) : []}
                      currentGuess={opponentCurrentGuess && opponentCurrentGuess.trim().length > 0 ? opponentCurrentGuess : ''}
                      maxAttempts={gameMode === 'race' ? 6 : 6}
                    />
                    {opponentCurrentGuess && opponentCurrentGuess.trim().length > 0 && (
                      <p className="text-sm text-gray-500 mt-2">
                        {gameState.player1Id === currentUserIdRef.current 
                          ? (gameState.player2Username || 'Gegner')
                          : (gameState.player1Username || 'Gegner')} tippt: {opponentCurrentGuess.replace(/\s/g, '_').toUpperCase()}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Alternating Mode Status */}
          {gameType === 'multiplayer' && gameMode === 'alternating' && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              {isMyTurn ? (
                <p className="text-blue-800 font-semibold">Dein Zug</p>
              ) : (
                <div>
                  <p className="text-gray-600">Warten auf {gameState.player1Id === currentUserIdRef.current 
                    ? (gameState.player2Username || 'Gegner')
                    : (gameState.player1Username || 'Gegner')}...</p>
                  {opponentCurrentGuess && (
                    <p className="text-sm text-gray-500 mt-2">
                      {gameState.player1Id === currentUserIdRef.current 
                        ? (gameState.player2Username || 'Gegner')
                        : (gameState.player1Username || 'Gegner')} tippt: {opponentCurrentGuess.replace(/\s/g, '_').toUpperCase()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {gameState.status === 'won' && gameType === 'solo' && (
            <div className="px-3 sm:px-4 py-3 rounded-lg mb-4 bg-green-100 border border-green-400 text-green-700">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm sm:text-base">Glückwunsch! Du hast das Wort erraten!</p>
                  {gameType === 'solo' && gameMode === 'daily' && timeUntilNextDaily && (
                    <p className="mt-2 text-xs sm:text-sm">
                      Nächstes Wort in: <span className="font-bold">{timeUntilNextDaily}</span>
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate('/menu')}
                  className="border-2 border-green-600 hover:border-green-700 bg-white hover:bg-green-50 text-green-600 hover:text-green-700 font-semibold py-2 px-4 rounded-lg transition duration-200 text-sm whitespace-nowrap self-start sm:self-auto min-h-[44px] flex-shrink-0"
                >
                  Zum Menü
                </button>
              </div>
            </div>
          )}

          {gameState.status === 'lost' && gameType === 'solo' && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-3 rounded-lg mb-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="mb-2 text-sm sm:text-base">Du hast verloren. Versuche es erneut!</p>
                  {!showSolution && (
                    <button
                      onClick={handleShowSolution}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 text-sm mt-2 min-h-[44px]"
                    >
                      Lösung anzeigen
                    </button>
                  )}
                  {showSolution && gameState.targetWord && (
                    <p className="mt-2 font-bold text-base sm:text-lg">Die Lösung war: {gameState.targetWord}</p>
                  )}
                  {gameMode === 'daily' && timeUntilNextDaily && (
                    <p className="mt-2 text-xs sm:text-sm">
                      Nächstes Wort in: <span className="font-bold">{timeUntilNextDaily}</span>
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate('/menu')}
                  className="border-2 border-red-600 hover:border-red-700 bg-white hover:bg-red-50 text-red-600 hover:text-red-700 font-semibold py-2 px-4 rounded-lg transition duration-200 text-sm whitespace-nowrap self-start sm:self-auto min-h-[44px] flex-shrink-0"
                >
                  Zum Menü
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-center mb-4 sm:mb-6 px-1">
            <WordleGrid
              wordLength={gameState.wordLength}
              attempts={
                // In race mode, only show own attempts. In alternating mode, show all attempts.
                gameType === 'multiplayer' && gameMode === 'race'
                  ? (gameState.attempts || []).filter(attempt => attempt.playerId === currentUserIdRef.current)
                  : (gameState.attempts || [])
              }
              currentGuess={isMyTurn ? currentGuess : ''}
              maxAttempts={gameType === 'solo' ? 6 : gameMode === 'race' ? 6 : 5}
              isAnimating={isAnimating}
              animatingCellIndex={animatingCellIndex}
              animatingFeedback={animatingFeedback}
              selectedCellIndex={isMyTurn ? (selectedCellIndex >= 0 ? selectedCellIndex : 0) : -1}
              onCellClick={isMyTurn ? handleCellClick : undefined}
              opponentCurrentGuess={!isMyTurn && (gameMode === 'race' || gameMode === 'alternating') ? opponentCurrentGuess : undefined}
            />
          </div>

          {!isRacePhase1 && isMyTurn && (
            <Keyboard
              onKeyPress={handleKeyPress}
              onEnter={isRacePhase2 ? handleMultiplayerGuess : handleEnter}
              onBackspace={handleBackspace}
              feedback={keyboardFeedback}
              useUmlauts={gameState.useUmlauts}
            />
          )}

          {canSkip && (
            <div className="mt-4 text-center">
              <button
                onClick={handleSkipTurn}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
              >
                Zug überspringen
              </button>
            </div>
          )}

          {gameState.status !== 'active' && gameMode === 'free' && gameType === 'solo' && (
            <div className="mt-6 text-center flex gap-4 justify-center">
              <button
                onClick={handleNewGame}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                Neues Spiel
              </button>
              <button
                onClick={() => navigate('/menu')}
                className="border-2 border-blue-500 hover:border-blue-700 bg-white hover:bg-blue-50 text-blue-600 hover:text-blue-700 font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                Zum Menü
              </button>
            </div>
          )}
        </div>
      </div>

      {showWordNotFoundModal && wordToAdd && (
        <WordNotFoundModal
          word={wordToAdd.word}
          wordLength={wordToAdd.wordLength}
          useUmlauts={wordToAdd.useUmlauts}
          onAdd={handleAddWord}
          onClose={() => {
            setShowWordNotFoundModal(false);
            setWordToAdd(null);
          }}
        />
      )}

      {/* Race Mode Winner Modal */}
      {(showRaceWinnerModal || (raceLoserChoice === 'show-result' && showRaceResult)) && gameType === 'multiplayer' && gameMode === 'race' && gameState && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={() => {
                setShowRaceWinnerModal(false);
                setRaceLoserChoice(null);
                setShowRaceResult(false);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              title="Schließen"
            >
              ×
            </button>
            {gameState.winnerId === currentUserIdRef.current ? (
              // Winner view
              <>
                <h2 className="text-2xl font-bold mb-4 text-gray-800">🎉 Du hast gewonnen!</h2>
                <p className="text-gray-600 mb-4">
                  {gameState.player1Id === currentUserIdRef.current 
                    ? (gameState.player2Username || 'Dein Gegner')
                    : (gameState.player1Username || 'Dein Gegner')} kann noch weiterspielen.
                </p>
                <p className="text-gray-600 mb-6">Du kannst live zuschauen, was {gameState.player1Id === currentUserIdRef.current 
                  ? (gameState.player2Username || 'dein Gegner')
                  : (gameState.player1Username || 'dein Gegner')} probiert.</p>
                <div className="mb-4">
                  <p className="text-gray-600 mb-4">Möchtest du nochmal spielen?</p>
                  <div className="flex gap-4">
                    <button
                      onClick={handleNewGameRequest}
                      disabled={newGameStatus === 'accepted' || newGameStatus === 'declined'}
                      className={`flex-1 py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                        newGameStatus === 'accepted'
                          ? 'bg-green-600 text-white'
                          : newGameStatus === 'declined'
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {newGameStatus === 'accepted' ? '✓ Zugestimmt' : 'Nochmal spielen'}
                    </button>
                    <button
                      onClick={() => {
                        setNewGameStatus('declined');
                        if (socket && gameState) {
                          socket.emit('new-game-decline', { gameId: gameState.gameId });
                        }
                        setTimeout(() => navigate('/menu'), 1000);
                      }}
                      disabled={newGameStatus === 'declined' || newGameStatus === 'accepted'}
                      className={`flex-1 py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                        newGameStatus === 'declined'
                          ? 'bg-red-600 text-white'
                          : newGameStatus === 'accepted'
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {newGameStatus === 'declined' ? '✗ Abgelehnt' : 'Ablehnen'}
                    </button>
                  </div>
                </div>
                {opponentNewGameStatus === 'pending' && (
                  <p className="mt-4 text-sm text-gray-500 text-center">Warten auf Antwort von {gameState.player1Id === currentUserIdRef.current 
                    ? (gameState.player2Username || 'Gegner')
                    : (gameState.player1Username || 'Gegner')}...</p>
                )}
                {opponentNewGameStatus === 'accepted' && newGameStatus === 'accepted' && (
                  <p className="mt-4 text-sm text-green-600 text-center font-semibold">Beide möchten nochmal spielen! Lobby wird erstellt...</p>
                )}
                {opponentNewGameStatus === 'declined' && (
                  <p className="mt-4 text-sm text-red-600 text-center font-semibold">{gameState.player1Id === currentUserIdRef.current 
                    ? (gameState.player2Username || 'Gegner')
                    : (gameState.player1Username || 'Gegner')} möchte nicht nochmal spielen.</p>
                )}
              </>
            ) : (
              // Loser view
              <>
                {!raceLoserChoice && (
                  <>
                    {/* Check if loser just guessed correctly */}
                    {gameState.attempts && gameState.attempts.length > 0 && 
                     gameState.attempts[gameState.attempts.length - 1]?.playerId === currentUserIdRef.current &&
                     gameState.attempts[gameState.attempts.length - 1]?.feedback?.every((f: string) => f === 'green') ? (
                      // Loser guessed correctly
                      <>
                        <h2 className="text-2xl font-bold mb-4 text-green-600">Super, du hast das Wort erraten!</h2>
                        <p className="text-gray-600 mb-6">Möchtest du eine neue Runde spielen?</p>
                        <div className="flex gap-4">
                          <button
                            onClick={handleNewGameRequest}
                            disabled={newGameStatus === 'accepted' || newGameStatus === 'declined'}
                            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                              newGameStatus === 'accepted'
                                ? 'bg-green-600 text-white'
                                : newGameStatus === 'declined'
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                          >
                            {newGameStatus === 'accepted' ? '✓ Zugestimmt' : 'Neue Runde'}
                          </button>
                          <button
                            onClick={() => {
                              setNewGameStatus('declined');
                              if (socket && gameState) {
                                socket.emit('new-game-decline', { gameId: gameState.gameId });
                              }
                              setTimeout(() => navigate('/menu'), 1000);
                            }}
                            disabled={newGameStatus === 'declined' || newGameStatus === 'accepted'}
                            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                              newGameStatus === 'declined'
                                ? 'bg-red-600 text-white'
                                : newGameStatus === 'accepted'
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700 text-white'
                            }`}
                          >
                            {newGameStatus === 'declined' ? '✗ Abgelehnt' : 'Ablehnen'}
                          </button>
                        </div>
                        {opponentNewGameStatus === 'pending' && (
                          <p className="mt-4 text-sm text-gray-500 text-center">Warten auf Antwort von {gameState.player1Id === currentUserIdRef.current 
                            ? (gameState.player2Username || 'Gegner')
                            : (gameState.player1Username || 'Gegner')}...</p>
                        )}
                        {opponentNewGameStatus === 'accepted' && newGameStatus === 'accepted' && (
                          <p className="mt-4 text-sm text-green-600 text-center font-semibold">Beide möchten nochmal spielen! Lobby wird erstellt...</p>
                        )}
                        {opponentNewGameStatus === 'declined' && (
                          <p className="mt-4 text-sm text-red-600 text-center font-semibold">{gameState.player1Id === currentUserIdRef.current 
                            ? (gameState.player2Username || 'Gegner')
                            : (gameState.player1Username || 'Gegner')} möchte nicht nochmal spielen.</p>
                        )}
                      </>
                    ) : (
                      // Loser hasn't guessed yet or hasn't finished
                      <>
                        <h2 className="text-2xl font-bold mb-4 text-red-600">
                          {gameState.player1Id === currentUserIdRef.current 
                            ? (gameState.player2Username || 'Dein Gegner')
                            : (gameState.player1Username || 'Dein Gegner')} hat gewonnen!
                        </h2>
                        <p className="text-gray-600 mb-6">Was möchtest du tun?</p>
                        <div className="flex flex-col gap-3">
                          <button
                            onClick={handleRaceLoserContinue}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                          >
                            Weiterraten
                          </button>
                          <button
                            onClick={handleRaceLoserShowResult}
                            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                          >
                            Ergebnis anzeigen
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
                {raceLoserChoice === 'show-result' && showRaceResult && (
                  <>
                    <div className="mb-4 p-4 bg-gray-100 rounded-lg">
                      <p className="font-semibold mb-2">Das Wort war:</p>
                      <p className="text-2xl font-bold text-gray-800">{gameState.opponentWord?.toUpperCase()}</p>
                    </div>
                    <p className="text-gray-600 mb-6">Was möchtest du tun?</p>
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={handleRaceLoserRematch}
                        disabled={rematchStatus === 'accepted' || rematchStatus === 'declined'}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                          rematchStatus === 'accepted'
                            ? 'bg-green-600 text-white'
                            : rematchStatus === 'declined'
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {rematchStatus === 'accepted' ? '✓ Zugestimmt' : 'Neues Spiel'}
                      </button>
                      <button
                        onClick={() => {
                          setRematchStatus('declined');
                          if (socket && gameState) {
                            socket.emit('rematch-decline', { gameId: gameState.gameId });
                          }
                          navigate('/menu');
                        }}
                        disabled={rematchStatus === 'declined' || rematchStatus === 'accepted'}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                          rematchStatus === 'declined'
                            ? 'bg-gray-600 text-white'
                            : rematchStatus === 'accepted'
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-gray-600 hover:bg-gray-700 text-white'
                        }`}
                      >
                        Zurück zum Hauptmenü
                      </button>
                    </div>
                    {opponentRematchStatus === 'pending' && (
                      <p className="mt-4 text-sm text-gray-500 text-center">Warten auf Antwort von {gameState.player1Id === currentUserIdRef.current 
                        ? (gameState.player2Username || 'Gegner')
                        : (gameState.player1Username || 'Gegner')}...</p>
                    )}
                    {opponentRematchStatus === 'accepted' && rematchStatus === 'accepted' && (
                      <p className="mt-4 text-sm text-green-600 text-center font-semibold">Beide möchten nochmal spielen! Lobby wird erstellt...</p>
                    )}
                    {opponentRematchStatus === 'declined' && (
                      <p className="mt-4 text-sm text-red-600 text-center font-semibold">{gameState.player1Id === currentUserIdRef.current 
                        ? (gameState.player2Username || 'Gegner')
                        : (gameState.player1Username || 'Gegner')} möchte nicht nochmal spielen.</p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Race Mode Lost Modal - when player used all 6 attempts without guessing */}
      {showRaceLostModal && gameType === 'multiplayer' && gameMode === 'race' && gameState && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={() => setShowRaceLostModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              title="Schließen"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold mb-4 text-red-600">Du hast verloren!</h2>
            <p className="text-gray-600 mb-4">
              Du hast alle 6 Versuche verbraucht. Du kannst dem Gegner noch zuschauen, aber das Wort war:
            </p>
            <div className="mb-4 p-4 bg-gray-100 rounded-lg">
              <p className="text-2xl font-bold text-gray-800">{gameState.opponentWord?.toUpperCase()}</p>
            </div>
            <p className="text-gray-600 mb-6">
              Du kannst weiterhin zuschauen, wie {gameState.player1Id === currentUserIdRef.current 
                ? (gameState.player2Username || 'dein Gegner')
                : (gameState.player1Username || 'dein Gegner')} spielt.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowRaceLostModal(false);
                  setRaceLoserChoice('continue');
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                Weiter zuschauen
              </button>
              <button
                onClick={() => {
                  setShowRaceLostModal(false);
                  navigate('/menu');
                }}
                className="flex-1 border-2 border-gray-500 hover:border-gray-700 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-700 font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                Zum Menü
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Game Modal */}
      {showNewGameModal && gameType === 'multiplayer' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={() => {
                setShowNewGameModal(false);
                setNewGameStatus(null);
                setOpponentNewGameStatus(null);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              title="Schließen"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Neues Spiel starten?</h2>
            <p className="text-gray-600 mb-6">
              {opponentNewGameStatus === 'pending' 
                ? `${gameState?.player1Id === currentUserIdRef.current 
                  ? (gameState?.player2Username || 'Der andere Spieler')
                  : (gameState?.player1Username || 'Der andere Spieler')} möchte ein neues Spiel starten.`
                : 'Möchtest du ein neues Spiel starten?'}
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleNewGameAccept}
                disabled={newGameStatus === 'accepted' || newGameStatus === 'declined'}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                  newGameStatus === 'accepted'
                    ? 'bg-green-600 text-white'
                    : newGameStatus === 'declined'
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {newGameStatus === 'accepted' ? '✓ Zugestimmt' : 'Ja'}
              </button>
              <button
                onClick={handleNewGameDecline}
                disabled={newGameStatus === 'declined' || newGameStatus === 'accepted'}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                  newGameStatus === 'declined'
                    ? 'bg-red-600 text-white'
                    : newGameStatus === 'accepted'
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {newGameStatus === 'declined' ? '✗ Abgelehnt' : 'Nein'}
              </button>
            </div>
            {newGameStatus === 'accepted' && opponentNewGameStatus === 'accepted' && (
              <p className="mt-4 text-sm text-green-600 text-center font-semibold">Beide haben zugestimmt! Weiterleitung zum Menü...</p>
            )}
            {newGameStatus === 'accepted' && opponentNewGameStatus === 'pending' && (
              <p className="mt-4 text-sm text-gray-500 text-center">Warten auf Antwort von {gameState?.player1Id === currentUserIdRef.current 
                ? (gameState?.player2Username || 'Gegner')
                : (gameState?.player1Username || 'Gegner')}...</p>
            )}
            {opponentNewGameStatus === 'accepted' && newGameStatus === 'pending' && (
              <p className="mt-4 text-sm text-gray-500 text-center">{gameState?.player1Id === currentUserIdRef.current 
                ? (gameState?.player2Username || 'Gegner')
                : (gameState?.player1Username || 'Gegner')} hat bereits zugestimmt. Bitte bestätige ebenfalls.</p>
            )}
            {opponentNewGameStatus === 'declined' && (
              <p className="mt-4 text-sm text-red-600 text-center font-semibold">{gameState?.player1Id === currentUserIdRef.current 
                ? (gameState?.player2Username || 'Gegner')
                : (gameState?.player1Username || 'Gegner')} möchte kein neues Spiel starten.</p>
            )}
          </div>
        </div>
      )}

      {/* Rematch Modal for Alternating Mode */}
      {showRematchModal && gameType === 'multiplayer' && gameMode === 'alternating' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={() => {
                setShowRematchModal(false);
                setRematchStatus(null);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              title="Schließen"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Glückwunsch! Dein Team hat das Wort erraten!</h2>
            <p className="text-gray-600 mb-6">Möchtest du nochmal spielen?</p>
            
            <div className="flex gap-4">
              <button
                onClick={handleRematchAccept}
                disabled={rematchStatus === 'accepted' || rematchStatus === 'declined'}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                  rematchStatus === 'accepted'
                    ? 'bg-green-600 text-white'
                    : rematchStatus === 'declined'
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {rematchStatus === 'accepted' ? '✓ Zugestimmt' : 'Nochmal spielen'}
              </button>
              <button
                onClick={handleRematchDecline}
                disabled={rematchStatus === 'declined' || rematchStatus === 'accepted'}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition duration-200 ${
                  rematchStatus === 'declined'
                    ? 'bg-red-600 text-white'
                    : rematchStatus === 'accepted'
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {rematchStatus === 'declined' ? '✗ Abgelehnt' : 'Ablehnen'}
              </button>
            </div>
            
            {opponentRematchStatus === 'pending' && (
              <p className="mt-4 text-sm text-gray-500 text-center">Warten auf Antwort von {gameState.player1Id === currentUserIdRef.current 
                ? (gameState.player2Username || 'Gegner')
                : (gameState.player1Username || 'Gegner')}...</p>
            )}
            {opponentRematchStatus === 'accepted' && rematchStatus !== 'accepted' && (
              <p className="mt-4 text-sm text-green-600 text-center font-semibold">{gameState.player1Id === currentUserIdRef.current 
                ? (gameState.player2Username || 'Gegner')
                : (gameState.player1Username || 'Gegner')} möchte nochmal spielen!</p>
            )}
            {opponentRematchStatus === 'declined' && (
              <p className="mt-4 text-sm text-red-600 text-center font-semibold">{gameState.player1Id === currentUserIdRef.current 
                ? (gameState.player2Username || 'Gegner')
                : (gameState.player1Username || 'Gegner')} möchte nicht nochmal spielen.</p>
            )}
          </div>
        </div>
      )}

      {/* Word Not In List Modal for Race Mode */}
      {showWordNotInListModal && pendingRaceWord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Wort nicht in der Liste</h2>
            <p className="text-gray-600 mb-6">
              Das Wort "<span className="font-semibold">{pendingRaceWord}</span>" ist nicht in der Wörterliste. Möchtest du es trotzdem setzen?
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleConfirmRaceWord}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                Ja, setzen
              </button>
              <button
                onClick={handleCancelRaceWord}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Game;
